import { ReactElement } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import { Check, Close } from '@mui/icons-material'

interface CourseGroup {
  courseID: string
  courseName: string
  userIDs: string[]
  userNames: string[]
}

interface BulkCourseCreditModalProps {
  isOpen: boolean
  courseGroups: Map<string, CourseGroup>
  totalUsers: number
  isSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
}

export default function BulkCourseCreditModal({
  isOpen,
  courseGroups,
  totalUsers,
  isSubmitting,
  onClose,
  onConfirm,
}: BulkCourseCreditModalProps): ReactElement {
  let totalCourses = courseGroups.size

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box className="flex items-center justify-between gap-2">
          <Typography variant="h6" component="h1">
            Give Credit
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="p-4">
        <Typography className="mb-4">
          You are about to give credit to <strong>{totalUsers}</strong> user(s)
          across <strong>{totalCourses}</strong> course(s):
        </Typography>

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {Array.from(courseGroups.values()).map((group) => (
            <Paper
              key={group.courseID}
              className="mb-3 p-3"
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <Typography variant="subtitle1" className="mb-2 font-bold">
                {group.courseName} ({group.userIDs.length} user
                {group.userIDs.length !== 1 ? 's' : ''})
              </Typography>
              <Box className="pl-4">
                {group.userNames.map((userName, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                  >
                    • {userName}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" className="mt-4">
          This will mark these courses as complete for the selected users. This
          action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions className="gap-2 p-4">
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Check />
            )
          }
        >
          {isSubmitting ? 'Processing...' : 'Give Credit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
